import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getWalletInfoByUserIdAPI, debitUserWalletAPI, getUserAvailableTermsByIdAPI } from '../../../../Library/apis';

const useTransaction = () => {
    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [amount, setAmount] = useState('');
    
    const [userWalletInfo, setUserWalletInfo] = useState({ savings: 0, terms: 0, interest: 0 });
    const [userTerms, setUserTerms] = useState([]);
    const [transactionConfig, setTransactionConfig] = useState({ type: '', wallet: '', balance: 0 });

    const userId = window.location.pathname.split('/').pop();

    const fetchDashboardData = useCallback(async (silent = false) => {
        if (!userId) return;
        if (!silent) setDataLoading(true);
        
        try {
            const [walletRes, termsRes] = await Promise.all([
                getWalletInfoByUserIdAPI(userId),
                getUserAvailableTermsByIdAPI(userId)
            ]);

            setUserWalletInfo({
                savings: walletRes.body?.totalSavingsWallet || 0,
                terms: walletRes.body?.totalTermSavingsWallet || 0,
                interest: walletRes.body?.totalInterestWallet || 0
            });
            setUserTerms(termsRes?.body || []);
        } catch (e) {
            toast.error("Failed to sync dashboard data");
        } finally {
            setDataLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const toggleModal = (type = '', wallet = '', balance = 0) => {
        setTransactionConfig({ type, wallet, balance });
        setAmount('');
        setModal(!modal);
    };

    const handleAmountChange = (e) => {
        let input = e.target.value.replace(/[^\d]*/g, '').padStart(3, '0');
        let integerPart = input.slice(0, -2);
        let decimalPart = input.slice(-2);
        setAmount(`${parseInt(integerPart, 10).toLocaleString('en-US')}.${decimalPart}`);
    };

    const executeTransfer = async () => {
        const numericAmount = parseFloat(amount.replace(/,/g, ''));
        if (numericAmount <= 0 || isNaN(numericAmount)) return toast.error("Enter valid amount");

        const result = await Swal.fire({
            title: 'Confirm Deduction',
            text: `Deduct ₦${amount} from this wallet?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, deduct it!'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                const response = await debitUserWalletAPI({
                    amount: numericAmount,
                    type: 'Savings',
                    userId: userId
                });

                if (response.status === 'Ok' || response.message === 'Wallet debited successfully') {
                    toast.success("Wallet updated successfully");
                    setModal(false);
                    // Silently refresh numbers across all cards
                    await fetchDashboardData(true); 
                } else {
                    toast.error(response.message || "Deduction failed");
                }
            } catch (err) {
                toast.error("Process failed.");
            } finally {
                setLoading(false);
            }
        }
    };

    return {
        modal, toggleModal, amount, handleAmountChange,
        userWalletInfo, userTerms, dataLoading, executeTransfer,
        loading, transactionConfig, userId,
        // This alias fixes the "fetchWalletInfo is not a function" error
        fetchWalletInfo: fetchDashboardData 
    };
};

export default useTransaction;